import { Modal, InputNumber, message, Alert } from 'antd';
import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  WARM_UP_ALIAS_MATCH_CACHE,
  AliasMatchCacheWarmUpSubmitResult,
} from '../graphql/mutations/warmUpAliasMatchCache';

interface WarmUpAliasCacheModalProps {
  visible: boolean;
  onClose: () => void;
}

// 单次预热的最大 id 跨度，与服务端 AddressAliasCacheWarmUpService.MAX_ID_SPAN 保持一致。
// 前端先拦一道，避免明显超限的请求白跑一趟后端再报错。
const MAX_ID_SPAN = 100000;

const WarmUpAliasCacheModal: React.FC<WarmUpAliasCacheModalProps> = ({ visible, onClose }) => {
  const [beginAliasId, setBeginAliasId] = useState<number | null>(null);
  const [endAliasId, setEndAliasId] = useState<number | null>(null);

  const [warmUpCache, { loading }] = useMutation(WARM_UP_ALIAS_MATCH_CACHE);

  // 弹窗关闭时重置输入，避免下次打开沿用上次内容
  useEffect(() => {
    if (!visible) {
      setBeginAliasId(null);
      setEndAliasId(null);
    }
  }, [visible]);

  const handleSubmit = async () => {
    if (beginAliasId === null || endAliasId === null) {
      message.error('请输入开始别名 id 和结束别名 id');
      return;
    }
    if (!Number.isInteger(beginAliasId) || !Number.isInteger(endAliasId)) {
      message.error('别名 id 必须是整数');
      return;
    }
    if (beginAliasId < 0) {
      message.error('开始别名 id 不能为负数');
      return;
    }
    if (endAliasId < beginAliasId) {
      message.error('结束别名 id 不能小于开始别名 id');
      return;
    }

    const span = endAliasId - beginAliasId + 1;
    if (span > MAX_ID_SPAN) {
      message.error(`单次 id 跨度不能超过 ${MAX_ID_SPAN}（当前 ${span}），请分多次执行`);
      return;
    }

    try {
      const { data } = await warmUpCache({
        variables: { beginAliasId, endAliasId },
      });
      const result: AliasMatchCacheWarmUpSubmitResult | undefined = data?.warmUpAliasMatchCache;
      if (!result) {
        message.error('提交失败：服务端未返回结果');
        return;
      }
      if (!result.accepted) {
        // 上一批仍在执行：本次什么都没做，保留弹窗让运维可以稍后重试
        message.warning(result.message, 6);
        return;
      }
      message.success(`${result.message}（runId: ${result.runId}）`, 8);
      onClose();
    } catch (err) {
      message.error(`初始化失败：${(err as Error).message}`);
    }
  };

  const span =
    beginAliasId !== null && endAliasId !== null && endAliasId >= beginAliasId
      ? endAliasId - beginAliasId + 1
      : null;

  return (
    <Modal
      title="别名缓存初始化"
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="提交初始化"
      okButtonProps={{ loading }}
      cancelButtonProps={{ disabled: loading }}
      cancelText="取消"
      width={600}
      maskClosable={!loading}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="按别名 id 区间预热匹配缓存"
        description={
          <>
            <div>会分批扫描别名与标准地址，把每个（别名 + 邮编）组合的最佳别名写入 Redis。</div>
            <div>仅对灰度名单内的邮编生效，已有缓存会被覆盖。</div>
            <div>单次 id 跨度不能超过 {MAX_ID_SPAN.toLocaleString()}，全量初始化请分多次执行。</div>
            <div>
              <b>提交后任务在后台执行</b>，接口立即返回；执行结果请凭返回的 runId 查服务端日志。
              上一批未跑完时会被拒绝，需等其完成后再提交。
            </div>
          </>
        }
      />

      <div style={{ marginBottom: 8 }}>开始别名 id：</div>
      <InputNumber
        style={{ width: '100%', marginBottom: 16 }}
        placeholder="例如：1"
        min={0}
        precision={0}
        value={beginAliasId}
        onChange={(value) => setBeginAliasId(value)}
        disabled={loading}
      />

      <div style={{ marginBottom: 8 }}>结束别名 id：</div>
      <InputNumber
        style={{ width: '100%' }}
        placeholder={`例如：${MAX_ID_SPAN.toLocaleString()}`}
        min={0}
        precision={0}
        value={endAliasId}
        onChange={(value) => setEndAliasId(value)}
        disabled={loading}
      />

      {span !== null && (
        <div
          style={{
            marginTop: 12,
            color: span > MAX_ID_SPAN ? '#ff4d4f' : 'rgba(0, 0, 0, 0.45)',
          }}
        >
          本次区间共 {span.toLocaleString()} 个 id
          {span > MAX_ID_SPAN ? `，已超过上限 ${MAX_ID_SPAN.toLocaleString()}` : ''}
        </div>
      )}
    </Modal>
  );
};

export default WarmUpAliasCacheModal;
